import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { axiosMaster } from "../api/axiosInstance";

const AddeditQuickAdd = ({ onClose, onSuccess }) => {

  /* ================= SAFE USER ================= */

  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const defaultRestId = storedUser?.restaurantId || "";
  const groupId = storedUser?.groupId || 1;
  const userId = storedUser?.userId || 0;

  /* ================= HELPERS ================= */

  const toTitleCase = (text) =>
    text
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .replace(/\s+/g, " ")
      .trimStart();

  /* ================= STATE ================= */

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [loadingRest, setLoadingRest] = useState(false);

  const [form, setForm] = useState({
    empName: "",
    empMobileNo: "",
    joiningDate: new Date().toISOString().slice(0, 10),
    restId: defaultRestId,
  });

  const [saving, setSaving] = useState(false);

  /* ================= REFS ================= */

  const restaurantRef = useRef(null);
  const nameRef = useRef(null);
  const mobileRef = useRef(null);
  const dateRef = useRef(null);
  const saveRef = useRef(null);

  /* ================= FETCH RESTAURANTS ================= */

  useEffect(() => {
    fetchRestaurants();

    setTimeout(() => {
      restaurantRef.current?.focus();
    }, 300);
  }, []);

  const fetchRestaurants = async () => {
    try {

      setLoadingRest(true);

      const res = await axiosMaster.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );

      const options = (res.data || []).map((r) => ({
        value: r.restId,
        label: r.restaurantName,
      }));

      setRestaurants(options);

      const found = options.find(
        (x) => Number(x.value) === Number(defaultRestId)
      );

      if (found) {
        setSelectedRestaurant(found);
      } else if (options.length) {
        setSelectedRestaurant(options[0]);
      }

    } catch (err) {

      console.error("Restaurant fetch error:", err);

    } finally {

      setLoadingRest(false);

    }
  };

  /* ================= FORM CHANGE ================= */

  const handleChange = (field, value) => {

    if (field === "empName") value = toTitleCase(value);

    if (field === "empMobileNo") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /* ================= RESTAURANT SELECT ================= */

  const handleRestaurantChange = (option) => {

    setSelectedRestaurant(option);

    setForm((prev) => ({
      ...prev,
      restId: option?.value || "",
    }));

    setTimeout(() => {
      nameRef.current?.focus();
    }, 80);
  };

  /* ================= KEYBOARD NAVIGATION ================= */

  const handleKey = (e, nextRef, prevRef) => {

    const isReactSelect =
      e.target?.id?.includes("react-select") ||
      e.target?.className?.includes("react-select");

    if (isReactSelect && e.key === "Enter") return;

    if (e.key === "Enter") {
      e.preventDefault();
      nextRef?.current?.focus();
    }

    if (e.key === "Escape") {
      e.preventDefault();

      if (prevRef?.current) {
        prevRef.current.focus();
      } else {
        onClose?.();
      }
    }
  };

  /* ================= SAVE ================= */

  const handleSave = async () => {

    if (!form.restId) {
      alert("Select restaurant");
      restaurantRef.current?.focus();
      return;
    }

    if (!form.empName.trim()) {
      alert("Enter employee name");
      nameRef.current?.focus();
      return;
    }

    if (!form.empMobileNo.trim()) {
      alert("Enter mobile number");
      mobileRef.current?.focus();
      return;
    }

    try {

      setSaving(true);

      const payload = {
        groupId,
        restId: Number(form.restId),
        empName: form.empName.trim(),
        empMobileNo: form.empMobileNo.trim(),
        joiningDate: new Date(form.joiningDate).toISOString(),
        userId,
      };

      await axiosMaster.post("/EmployeeMaster/QuickAdd", payload);

      onSuccess?.();
      onClose?.();

    } catch (err) {

      console.error("Quick add error:", err);
      alert("Quick add failed");

    } finally {

      setSaving(false);

    }
  };

  /* ================= UI ================= */

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">

      <div className="bg-white w-full max-w-md rounded-xl shadow-xl">

        {/* HEADER */}

        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="font-bold text-lg">⚡ Quick Add Employee</h2>
          <button onClick={onClose} className="text-red-500 text-xl font-bold">
            ✕
          </button>
        </div>

        {/* BODY */}

        <div className="p-4 space-y-4">

          {/* RESTAURANT */}

          <div>
            <label className="text-sm font-medium">Restaurant *</label>

            <Select
              ref={restaurantRef}
              options={restaurants}
              value={selectedRestaurant}
              onChange={handleRestaurantChange}
              onKeyDown={(e) => handleKey(e, nameRef, null)}
              isLoading={loadingRest}
              placeholder="Select Restaurant"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              isSearchable
              openMenuOnFocus={true}
              tabSelectsValue={false}
              menuPosition="fixed"
            />
          </div>

          {/* NAME */}

          <div>
            <label className="text-sm font-medium">Employee Name *</label>

            <input
              ref={nameRef}
              value={form.empName}
              onChange={(e) => handleChange("empName", e.target.value)}
              onKeyDown={(e) => handleKey(e, mobileRef, restaurantRef)}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </div>

          {/* MOBILE */}

          <div>
            <label className="text-sm font-medium">Mobile *</label>

            <input
              ref={mobileRef}
              maxLength={10}
              value={form.empMobileNo}
              onChange={(e) => handleChange("empMobileNo", e.target.value)}
              onKeyDown={(e) => handleKey(e, dateRef, nameRef)}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </div>

          {/* DATE */}

          <div>
            <label className="text-sm font-medium">Joining Date</label>

            <input
              ref={dateRef}
              type="date"
              value={form.joiningDate}
              onChange={(e) => handleChange("joiningDate", e.target.value)}
              onKeyDown={(e) => handleKey(e, saveRef, mobileRef)}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </div>

        </div>

        {/* FOOTER */}

        <div className="flex justify-end gap-2 p-4 border-t">

          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border"
          >
            Cancel
          </button>

          <button
            ref={saveRef}
            onClick={handleSave}
            onKeyDown={(e) => {
              // ENTER → Save
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }

              // ESC → go back to date
              if (e.key === "Escape") {
                e.preventDefault();
                dateRef.current?.focus();
              }
            }}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>

        </div>

      </div>

    </div>
  );
};

export default AddeditQuickAdd;